import { observable, action, makeObservable } from 'mobx';
import { toast, toastTypeEnum } from 'common/utils';
import { getApiErrorMessage } from 'services/utils';

import {
  getAllStaticAsset,
  addAsset,
  updateAsset,
  deleteAsset,
  orderAsset
} from 'services/staticAsset';

export type IInitialState = {
  data: {
    tabel: 'VIDEO' | 'SHORTCUT_KEYS';
    shortcut_keys: [];
    trims: [];
    videos: [];
    limit: number;
    search: string;
    offset: number;
    total: number;
  };
  ui: {
    assetLoading: boolean;
  };
};

export const initialState: IInitialState = {
  data: {
    tabel: 'VIDEO',
    shortcut_keys: [],
    trims: [],
    videos: [],
    search: '',
    limit: 10,
    offset: 1,
    total: 0
  },
  ui: {
    assetLoading: false
  }
};

export class StaticAssetStore {
  state: IInitialState = initialState;

  getAllAsset = async (
    search = '',
    limit = 10,
    offset = 1,
    assetType: 'VIDEO' | 'SHORTCUT_KEYS'
  ) => {
    try {
      this.setState({
        ui: { ...this.state.ui, assetLoading: true },
        data: { ...this.state.data, tabel: assetType }
      });
      const { data } = await getAllStaticAsset({
        assetType,
        ...{ search: search, limit },
        offset: (offset - 1) * limit
      });
      console.log(data);
      this.setState({
        data: {
          ...this.state.data,
          ...(assetType === 'VIDEO'
            ? { videos: data?.videos?.rows || [] }
            : {}),
          // ...(assetType === 'SHORTCUT_KEYS' ? { shortcut_keys: data.rows } : {}),
          total: data.videos.count,
          limit: limit,
          offset: offset
        }
      });
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, assetLoading: false } });
    }
  };

  addAsset = async (data: IAssetParam) => {
    try {
      this.setState({ ui: { ...this.state.ui, assetLoading: true } });
      await addAsset(data);
      const {
        data: { limit, search, offset }
      } = this.state;
      await this.getAllAsset(search, limit, offset, 'VIDEO');
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, assetLoading: false } });
    }
  };

  updateAsset = async (data: IAssetParam) => {
    try {
      this.setState({ ui: { ...this.state.ui, assetLoading: true } });
      await updateAsset(data);
      const {
        data: { limit, search, offset }
      } = this.state;
      await this.getAllAsset(search, limit, offset, 'VIDEO');
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, assetLoading: false } });
    }
  };

  deleteAsset = async (data: IAssetDeleteParam) => {
    try {
      this.setState({ ui: { ...this.state.ui, assetLoading: true } });
      await deleteAsset(data);
      const {
        data: { limit, search, offset }
      } = this.state;
      await this.getAllAsset(search, limit, offset, 'VIDEO');
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, assetLoading: false } });
    }
  };

  orderAsset = async (data: {
    index: number;
    id: string;
    assetType: string;
  }) => {
    try {
      this.setState({ ui: { ...this.state.ui, assetLoading: true } });
      await orderAsset(data);
      const {
        data: { limit, search, offset }
      } = this.state;
      await this.getAllAsset(search, limit, offset, 'VIDEO');
    } catch (error) {
      const message = getApiErrorMessage(error);
      toast(toastTypeEnum.error, message);
    } finally {
      this.setState({ ui: { ...this.state.ui, assetLoading: false } });
    }
  };

  setState = (params: Partial<IInitialState>) => {
    const { state } = this;
    this.state = {
      ...state,
      ...params
    };
    /* use this space to save/sync state with localStorage */
  };

  reset = () => {
    this.state = initialState;
    /* use this space to reset localStorage in case if you're persisting this state */
  };

  constructor() {
    makeObservable(this, {
      state: observable,
      getAllAsset: action,
      addAsset: action,
      updateAsset: action,
      deleteAsset: action,
      orderAsset: action,
      setState: action,
      reset: action
    });
  }
}

type IAssetParam = {
  assetType: 'VIDEO' | 'SHORTCUT_KEYS';
  en_title: string;
  en_summary: string;
  zh_title?: string;
  zh_summary?: string;
  src?: string;
  thumb?: string;
};

type IAssetDeleteParam = {
  id: string;
  assetType: 'VIDEO' | 'SHORTCUT_KEYS';
};
